/**
 * Story 054 — ISO 3166-1 country list.
 *
 * Trimmed to the codes likely to appear in a wind-farm context (every
 * country with operating offshore + onshore farms). Bumping to the full
 * 249 ISO codes is a one-PR change when a tenant complains.
 *
 * 2026-05-15 — the BE DB (`tenant_<id>.sites.country_code`) actually
 * stores **alpha-2** values (`DE`, `AT`, `GB`, …) despite the
 * `assets/v1@1.0.0` contract declaring alpha-3. `countryByCode()`
 * accepts both lengths and falls back to a parallel alpha-2 table so
 * the column renders the full name regardless of which side fixes the
 * contract drift first.
 *
 * 2026-05-19 (R58-W-02) — the contract caught up. `assets/v1@4.2.0`
 * (via the FEAT-010 / 2.0.0 narrowing) declares `countryCode` as
 * alpha-2 only. The Add/Edit-site autocomplete now sources from
 * `ISO_3166_ALPHA2`; the legacy `ISO_3166_ALPHA3` list is retained
 * for `countryByCode` / `toAlpha2` (which still accept both inputs).
 */
export interface CountryOption {
  code: string;
  name: string;
}

const ALPHA2_TO_NAME: Record<string, string> = {
  AE: "United Arab Emirates",
  AR: "Argentina",
  AT: "Austria",
  AU: "Australia",
  BA: "Bosnia and Herzegovina",
  BE: "Belgium",
  BG: "Bulgaria",
  BR: "Brazil",
  CA: "Canada",
  CH: "Switzerland",
  CL: "Chile",
  CN: "China",
  CO: "Colombia",
  CR: "Costa Rica",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DK: "Denmark",
  DO: "Dominican Republic",
  EC: "Ecuador",
  EE: "Estonia",
  EG: "Egypt",
  ES: "Spain",
  ET: "Ethiopia",
  FI: "Finland",
  FR: "France",
  GB: "United Kingdom",
  GR: "Greece",
  HR: "Croatia",
  HU: "Hungary",
  ID: "Indonesia",
  IE: "Ireland",
  IL: "Israel",
  IN: "India",
  IS: "Iceland",
  IT: "Italy",
  JO: "Jordan",
  JP: "Japan",
  KE: "Kenya",
  KR: "South Korea",
  KZ: "Kazakhstan",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  MA: "Morocco",
  ME: "Montenegro",
  MX: "Mexico",
  NL: "Netherlands",
  NO: "Norway",
  NZ: "New Zealand",
  PA: "Panama",
  PE: "Peru",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Poland",
  PT: "Portugal",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russia",
  SA: "Saudi Arabia",
  SE: "Sweden",
  SG: "Singapore",
  SI: "Slovenia",
  SK: "Slovakia",
  TH: "Thailand",
  TN: "Tunisia",
  TR: "Türkiye",
  TW: "Taiwan",
  UA: "Ukraine",
  US: "United States",
  UY: "Uruguay",
  VN: "Viet Nam",
  ZA: "South Africa",
  // The DB also contains a literal `AG` row that resolves to Antigua
  // & Barbuda in ISO; harmless if a different tenant adopts it.
  AG: "Antigua and Barbuda",
};

/**
 * Alpha-2 option list -- the canonical source for the Add/Edit Site
 * country autocomplete under `assets/v1@4.2.0`. Derived from
 * `ALPHA2_TO_NAME` so the two stay in lockstep (one source, two
 * shapes). Sorted by name for human-friendly picker UX.
 */
export const ISO_3166_ALPHA2: CountryOption[] = Object.entries(ALPHA2_TO_NAME)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const ISO_3166_ALPHA3: CountryOption[] = [
  { code: "ARE", name: "United Arab Emirates" },
  { code: "ARG", name: "Argentina" },
  { code: "AUS", name: "Australia" },
  { code: "AUT", name: "Austria" },
  { code: "BEL", name: "Belgium" },
  { code: "BGR", name: "Bulgaria" },
  { code: "BRA", name: "Brazil" },
  { code: "CAN", name: "Canada" },
  { code: "CHE", name: "Switzerland" },
  { code: "CHL", name: "Chile" },
  { code: "CHN", name: "China" },
  { code: "COL", name: "Colombia" },
  { code: "CRI", name: "Costa Rica" },
  { code: "CYP", name: "Cyprus" },
  { code: "CZE", name: "Czechia" },
  { code: "DEU", name: "Germany" },
  { code: "DNK", name: "Denmark" },
  { code: "DOM", name: "Dominican Republic" },
  { code: "ECU", name: "Ecuador" },
  { code: "EGY", name: "Egypt" },
  { code: "ESP", name: "Spain" },
  { code: "EST", name: "Estonia" },
  { code: "ETH", name: "Ethiopia" },
  { code: "FIN", name: "Finland" },
  { code: "FRA", name: "France" },
  { code: "GBR", name: "United Kingdom" },
  { code: "GRC", name: "Greece" },
  { code: "HRV", name: "Croatia" },
  { code: "HUN", name: "Hungary" },
  { code: "IDN", name: "Indonesia" },
  { code: "IND", name: "India" },
  { code: "IRL", name: "Ireland" },
  { code: "ISL", name: "Iceland" },
  { code: "ISR", name: "Israel" },
  { code: "ITA", name: "Italy" },
  { code: "JOR", name: "Jordan" },
  { code: "JPN", name: "Japan" },
  { code: "KAZ", name: "Kazakhstan" },
  { code: "KEN", name: "Kenya" },
  { code: "KOR", name: "South Korea" },
  { code: "LTU", name: "Lithuania" },
  { code: "LUX", name: "Luxembourg" },
  { code: "LVA", name: "Latvia" },
  { code: "MAR", name: "Morocco" },
  { code: "MEX", name: "Mexico" },
  { code: "MNE", name: "Montenegro" },
  { code: "NLD", name: "Netherlands" },
  { code: "NOR", name: "Norway" },
  { code: "NZL", name: "New Zealand" },
  { code: "PAK", name: "Pakistan" },
  { code: "PAN", name: "Panama" },
  { code: "PER", name: "Peru" },
  { code: "PHL", name: "Philippines" },
  { code: "POL", name: "Poland" },
  { code: "PRT", name: "Portugal" },
  { code: "ROU", name: "Romania" },
  { code: "RUS", name: "Russia" },
  { code: "SAU", name: "Saudi Arabia" },
  { code: "SGP", name: "Singapore" },
  { code: "SRB", name: "Serbia" },
  { code: "SVK", name: "Slovakia" },
  { code: "SVN", name: "Slovenia" },
  { code: "SWE", name: "Sweden" },
  { code: "THA", name: "Thailand" },
  { code: "TUN", name: "Tunisia" },
  { code: "TUR", name: "Türkiye" },
  { code: "TWN", name: "Taiwan" },
  { code: "UKR", name: "Ukraine" },
  { code: "URY", name: "Uruguay" },
  { code: "USA", name: "United States" },
  { code: "VNM", name: "Viet Nam" },
  { code: "ZAF", name: "South Africa" },
];

export function countryByCode(
  code: string | null | undefined,
): CountryOption | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  // Alpha-3 first (canonical contract shape).
  const alpha3 = ISO_3166_ALPHA3.find((c) => c.code === upper);
  if (alpha3) return alpha3;
  // Alpha-2 fallback — what the BE DB actually stores today.
  if (upper.length === 2 && ALPHA2_TO_NAME[upper]) {
    return { code: upper, name: ALPHA2_TO_NAME[upper] };
  }
  return { code, name: code };
}

/**
 * Alpha-3 → alpha-2 mapping for the wind-context subset above. Used
 * to FORCE alpha-2 display in user-facing surfaces (the Country
 * filter chip / dropdown label) even when the BE currently still
 * emits alpha-3 strings in its `availableCountries` rollup. After
 * `assets/v1@2.0.0` lands the BE emits alpha-2 natively (locked
 * 2026-05-15 per `project_assets_alpha2_decision`) and this map
 * becomes a no-op fallback.
 */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  ARE: "AE",
  ARG: "AR",
  AUS: "AU",
  AUT: "AT",
  BEL: "BE",
  BGR: "BG",
  BIH: "BA",
  BRA: "BR",
  CAN: "CA",
  CHE: "CH",
  CHL: "CL",
  CHN: "CN",
  COL: "CO",
  CRI: "CR",
  CYP: "CY",
  CZE: "CZ",
  DEU: "DE",
  DNK: "DK",
  DOM: "DO",
  ECU: "EC",
  EGY: "EG",
  ESP: "ES",
  EST: "EE",
  ETH: "ET",
  FIN: "FI",
  FRA: "FR",
  GBR: "GB",
  GRC: "GR",
  HRV: "HR",
  HUN: "HU",
  IDN: "ID",
  IND: "IN",
  IRL: "IE",
  ISL: "IS",
  ISR: "IL",
  ITA: "IT",
  JOR: "JO",
  JPN: "JP",
  KAZ: "KZ",
  KEN: "KE",
  KOR: "KR",
  LTU: "LT",
  LUX: "LU",
  LVA: "LV",
  MAR: "MA",
  MEX: "MX",
  MNE: "ME",
  NLD: "NL",
  NOR: "NO",
  NZL: "NZ",
  PAK: "PK",
  PAN: "PA",
  PER: "PE",
  PHL: "PH",
  POL: "PL",
  PRT: "PT",
  ROU: "RO",
  RUS: "RU",
  SAU: "SA",
  SGP: "SG",
  SRB: "RS",
  SVK: "SK",
  SVN: "SI",
  SWE: "SE",
  THA: "TH",
  TUN: "TN",
  TUR: "TR",
  TWN: "TW",
  UKR: "UA",
  URY: "UY",
  USA: "US",
  VNM: "VN",
  ZAF: "ZA",
};

/**
 * Return the alpha-2 form of a country code, accepting either an
 * alpha-2 (passthrough) or alpha-3 (mapped) input. Falls back to the
 * uppercased input when no mapping exists.
 */
export function toAlpha2(code: string): string {
  const upper = code.toUpperCase();
  if (upper.length === 2) return upper;
  return ALPHA3_TO_ALPHA2[upper] ?? upper;
}

/**
 * Alpha-2 → alpha-3 mapping for the wind-context subset above, derived
 * by inverting `ALPHA3_TO_ALPHA2` (single source of truth — the two
 * stay in lockstep). Used by the country-autocomplete `filterOptions`
 * so a user can type the alpha-3 code ("AUT") and still resolve the
 * stored alpha-2 value ("AT"). Returns `undefined` when no alpha-3 is
 * known for the alpha-2 input.
 */
const ALPHA2_TO_ALPHA3: Record<string, string> = Object.fromEntries(
  Object.entries(ALPHA3_TO_ALPHA2).map(([a3, a2]) => [a2, a3]),
);

/**
 * Return the ISO 3166-1 alpha-3 code for an alpha-2 input (e.g.
 * "AT" → "AUT"), or `undefined` when the wind-context subset doesn't
 * carry it. Case-insensitive.
 */
export function toAlpha3(alpha2: string): string | undefined {
  return ALPHA2_TO_ALPHA3[alpha2.toUpperCase()];
}
